import { supabase } from './supabase'

export type StudentBalance = {
  id: string
  name: string
  tutorId: string | null
  balanceMinutes: number
  sessionLengthMinutes: number
  pendingMinutes: number
  availableMinutes: number
}

export async function fetchStudentBalances(): Promise<StudentBalance[]> {
  const { data, error } = await supabase.from('student_balances').select('*').order('name')
  if (error) throw error
  return (data ?? []).map((r: any) => ({
    id: r.id,
    name: r.name,
    tutorId: r.tutor_id,
    balanceMinutes: r.balance_minutes ?? 0,
    sessionLengthMinutes: r.session_length_minutes ?? 60,
    pendingMinutes: r.pending_minutes ?? 0,
    availableMinutes: r.available_minutes ?? 0,
  }))
}

export function isLogBlocked(b: Pick<StudentBalance, 'availableMinutes'> | undefined): boolean {
  return !!b && b.availableMinutes <= 0
}

export function approvalNeedsReason(balanceMinutes: number | undefined, overBalance: boolean | undefined): boolean {
  return !!overBalance || (balanceMinutes !== undefined && balanceMinutes <= 0)
}

export function sessionsLabel(minutes: number, sessionLength: number): string {
  const n = Math.floor(Math.max(minutes, 0) / Math.max(sessionLength || 60, 1))
  if (n === 0) return 'no sessions'
  return `${n} session${n === 1 ? '' : 's'}`
}

export function isBalanceGuardError(e: any): boolean {
  return e?.hint === 'zero_balance' || e?.hint === 'override_reason_required'
}

export type ReportSession = {
  tutorId: string | null
  studentId: string | null
  minutes: number
  approvalStatus: string | null
  lifecycleStatus: string | null
  isFree: boolean
  overBalance: boolean
}

export type TutorBalanceRow = {
  tutorId: string
  tutorName: string
  sessions: number
  minutes: number
  approved: number
  pending: number
  overBalance: number
  studentsAtZero: { id: string; name: string; balanceMinutes: number }[]
  minutesOwed: number
}

export function buildTutorBalanceReport(
  sessions: ReportSession[],
  balances: StudentBalance[],
  tutors: { id: string; name: string }[]
): TutorBalanceRow[] {
  const balanceById = new Map(balances.map(b => [b.id, b]))
  const rows = new Map<string, TutorBalanceRow & { studentIds: Set<string> }>()

  for (const s of sessions) {
    if (!s.tutorId || s.lifecycleStatus === 'cancelled' || s.isFree) continue
    let row = rows.get(s.tutorId)
    if (!row) {
      row = {
        tutorId: s.tutorId,
        tutorName: tutors.find(t => t.id === s.tutorId)?.name ?? 'Unknown tutor',
        sessions: 0, minutes: 0, approved: 0, pending: 0, overBalance: 0,
        studentsAtZero: [], minutesOwed: 0, studentIds: new Set(),
      }
      rows.set(s.tutorId, row)
    }
    row.sessions += 1
    row.minutes += s.minutes || 0
    if (s.approvalStatus === 'approved') row.approved += 1
    if (s.approvalStatus === 'pending') row.pending += 1
    if (s.overBalance) row.overBalance += 1
    if (s.studentId) row.studentIds.add(s.studentId)
  }

  return Array.from(rows.values())
    .map(({ studentIds, ...row }) => {
      const atZero = Array.from(studentIds)
        .map(id => balanceById.get(id))
        .filter((b): b is StudentBalance => !!b && b.balanceMinutes <= 0)
        .map(b => ({ id: b.id, name: b.name, balanceMinutes: b.balanceMinutes }))
        .sort((a, b) => a.balanceMinutes - b.balanceMinutes)
      return {
        ...row,
        studentsAtZero: atZero,
        minutesOwed: atZero.reduce((sum, b) => sum + Math.max(-b.balanceMinutes, 0), 0),
      }
    })
    .sort((a, b) => b.overBalance - a.overBalance || b.studentsAtZero.length - a.studentsAtZero.length || a.tutorName.localeCompare(b.tutorName))
}

export type BalanceQuality = 'exact' | 'estimated' | 'current'

export async function fetchStudentBalancesAt(at: Date): Promise<{ balances: StudentBalance[]; quality: Record<string, BalanceQuality> }> {
  const { data, error } = await supabase.rpc('student_balances_at', { p_at: at.toISOString() })
  if (error) throw error
  const quality: Record<string, BalanceQuality> = {}
  const balances = (data ?? []).map((r: any) => {
    quality[r.id] = r.quality
    return {
      id: r.id,
      name: r.name,
      tutorId: r.tutor_id,
      balanceMinutes: r.balance_minutes ?? 0,
      sessionLengthMinutes: r.session_length_minutes ?? 60,
      pendingMinutes: 0,
      availableMinutes: r.balance_minutes ?? 0,
    } as StudentBalance
  })
  return { balances, quality }
}

export function reportCutoff(monthStart: Date, now: Date = new Date()): Date {
  const end = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 1)
  end.setMilliseconds(-1)
  return end.getTime() < now.getTime() ? end : now
}
